import { IButton, ILocation, IMessageItem, MessageType, IReplyMessageItem } from "../~store/models/MessageItem";
import './MessageItem.css';
import { GET_FILE_BY_ID_API_ROUTE } from "../~api/apiRoutes";
import AuthorName from "./AuthorName/AuthorName";
import { getTime, getDateTime } from "src/app/shared/helpers/date-helper/DateFormats";
import { OperatorType } from "../~store/models/OperatorItem";
import { useNavigate } from "react-router-dom";
import RetryButton from '@mui/icons-material/ReplaySharp';
import { TopicPairContentType } from "src/app/nlp/~store/models/enums/TopicPairContentType";
import { ConvertTextToContentIcon } from "src/app/shared/components/text-to-content/TextToContent";
import { useTranslation } from 'react-i18next';
import { NLP_SEARCH_TOPIC_PATH } from "src/app/AppConstants";
import { OperatorStatusType } from "../~store/state/ChatItemsState";
import { MapProvider } from "src/app/chats/~store/models/enums/MapProvider";
import HyperlinkTextHelper from "src/app/shared/helpers/url-helper/HyperlinkTextHelper";
import { IMessageItemProps } from "./~types/MessageItemProps";
import { UnsupportedAudioMimeTypes } from "./~types/UnsupportedAudioMimeTypes";
import { MouseEvent, useState, type JSX } from "react";
import { Menu, MenuItem, PopoverPosition } from "@mui/material";
import { formatQuote } from "src/app/shared/helpers/quote-helper/QuoteHelper";
import { ChannelType } from "src/app/channel/~store/models/enums/ChannelType";
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { DeletedMessagesDisplay } from "../../chat/models/enums/DeletedMessagesDisplay";

const MessageItem = (props: IMessageItemProps) => {

    const { t } = useTranslation();
    let mt = "";
    let mrt = "";
    const userInStorage = sessionStorage.getItem("user");
    const customerId = userInStorage === null ? "" : JSON.parse(userInStorage.toString()).customerId;
    const navigate = useNavigate();
    const needPreWrap = /[ \t]{2,}|\n\s*\n/.test(props.messageItem.text ?? "");
    const [isReplyExpanded, setIsReplyExpanded] = useState(false);

    switch (props.messageItem.messageType) {
        case MessageType.operatorMessage:
        case MessageType.clientSystemMessage:
            {
                if(props.messageItem.operatorModel?.type === OperatorType.Bot)
                {
                    mt = "operator-message-type bot-message"
                }
                else
                {
                    mt = "operator-message-type";
                }
            break;
            }
        case MessageType.clientMessage:
            mt = "client-message-type";
            break;
    }

    if (props.messageItem.forOperatorsOnly)
        mt += "operator-message-type comment-message";

    const onRemoveOperatorHandler = () => {
        const { operatorId, chatId } = props.messageItem;
        props.onRemoveOperator(operatorId ?? 0, chatId);
    }

    const mapFileToComponent = (message: IMessageItem, contentType: TopicPairContentType) =>
    {
        if (message.fileId?.length > 0) {
            if ((message.fileInfo.mimeType?.startsWith("audio/") || message.contentType === TopicPairContentType.Audio) && !UnsupportedAudioMimeTypes.has(message.fileInfo.mimeType)) {
                return (
                    <audio
                        src={GET_FILE_BY_ID_API_ROUTE + message.fileId + "&customerId=" + customerId}
                        controls
                        onPlay={(e) => props.onMediaPlay?.(e.currentTarget)}
                    />
                );
            }
            else if (message.fileInfo.mimeType?.startsWith("video/") || message.contentType === TopicPairContentType.Video) {
                return (
                    <video
                        className="message-item__media"
                        src={GET_FILE_BY_ID_API_ROUTE + message.fileId + "&customerId=" + customerId}
                        autoPlay={false}
                        loop={false}
                        preload="none"
                        controls={true}
                        onPlay={(e) => props.onMediaPlay?.(e.currentTarget)}
                    />
                );
            }
            else {
                return (
                    <div className="message-item__file_link">
                        <a href={GET_FILE_BY_ID_API_ROUTE + message.fileId + "&customerId=" + customerId}>{message.fileInfo.fileName}</a>
                    </div>
                );
            }
        }
    }

    const convertButtonTextToButtons = (buttonText: string, key: number) => {
        return <div className="operator-button-display" key={key}> {buttonText} </div>
    }

    const buttonDisplay = (buttons: IButton[]) => {
        if (buttons === undefined || buttons === null || buttons.length == 0) {
            return;
        }
        
        var buttonsArray: JSX.Element[]= [];
        buttons.forEach((button, index) => buttonsArray.push(convertButtonTextToButtons(button.text, index)));
        return buttonsArray;
    }

    const retryButtonComponent = () => {
        if(props.operatorStatus === OperatorStatusType.Offline)
        {
            return;
        }
        
        if (props.messageItem.isFailed && props.messageItem.operatorId === props.operatorId && !(props.selectedChat?.isClosed ?? false) && props.selectedChat?.chatId === props.messageItem.chatId)
        return (
            <RetryButton titleAccess={t('chat.resendMessage')} className="retry-send-button" color={ "error" } onClick={() => props.resendMessage(props.messageItem.messageId)}></RetryButton>
        );
    }

    const getMapUrl = (location: ILocation) =>
    {
        if (props.mapSettings?.mapProvider == MapProvider.Yandex)
        {
            return "https://yandex.ru/map-widget/v1/?ll=" + location?.longitude + "," + location?.latitude + "&z=15&pt=" + location?.longitude + "," + location?.latitude + ",pm2rdm";
        }
        else
        {
            return "https://maps.google.com/maps?q=" + location?.latitude + "," + location?.longitude + "&z=15&output=embed";
        }
    }

    const getMapOpenUrl = (location: ILocation) => {
        if (props.mapSettings?.mapProvider == MapProvider.Yandex) {
            return `https://yandex.ru/maps/?pt=${location?.longitude},${location?.latitude}&z=15&ll=${location?.longitude},${location?.latitude}`;
        }
        else {
            return `https://www.google.com/maps?q=${location?.latitude},${location?.longitude}`;
        }
    };

    const hasFileOrImage = !!props.messageItem.fileId || !!props.messageItem.imageId;

    const { content, icon } = ConvertTextToContentIcon(
        props.messageItem.contentType,
        props.messageItem.messageType,
        props.messageItem.text,
        props.messageItem.operatorModel?.type === OperatorType.Bot,
        hasFileOrImage
    );

    const isAudioOrVideo =
        props.messageItem.contentType === TopicPairContentType.Audio ||
        props.messageItem.contentType === TopicPairContentType.Video;

    const textContent = isAudioOrVideo
        ? ConvertTextToContentIcon(
            TopicPairContentType.Markup,
            props.messageItem.messageType,
            props.messageItem.text,
            props.messageItem.operatorModel?.type === OperatorType.Bot,
            hasFileOrImage
        ).content
        : content;

    const readIcon = props.messageItem.messageType === MessageType.operatorMessage
        ? props.messageItem.readTimestamp === null ? <DoneRoundedIcon className="message-read-icon"/> : <DoneAllRoundedIcon className="message-read-icon"/>
        : null;

    const editButtonComponent = () => {
        if (props.messageItem.messageType === MessageType.operatorMessage &&
            props.messageItem.operatorId === props.operatorId &&
            !props.messageItem.isDeleted &&
            !props.messageItem.isFailed &&
            !(props.selectedChat?.isClosed ?? false) &&
            props.messageItem.chatId === props.selectedChat?.chatId) {
            return (
                <div
                    className="message-edit-icon"
                    data-testid="message-edit"
                    onClick={() => props.onEditMessage(props.messageItem)}
                >
                    <i className="material-icons">edit</i>
                </div>
            );
        }
    };

    const getReplyPreviewText = (reply?: IReplyMessageItem): string => {
        if (!reply) return "";
        if (props.deletedMessagesDisplay == DeletedMessagesDisplay.Hide && reply.isDeleted) return t("chat.deletedMessage");
        if (reply.text && reply.text.trim().length > 0) return reply.text;
        return "";
    };

    const getReplyAuthor = (reply?: IReplyMessageItem) => {
        if (!reply) return "";
        return reply.operatorName?.trim() || reply.senderName;
    };

    switch (props.messageItem.replyToMessage?.messageType) {
        case MessageType.operatorMessage:
        case MessageType.clientSystemMessage:
            {
                mrt = "message-reply-preview--operator";
                break;
            }
        case MessageType.clientMessage:
            mrt = "message-reply-preview--client";
            break;
    }

    const getReplyAttachmentUrl = (reply?: IReplyMessageItem): string => {
        if (!reply) return "";

        if (reply.imageId) {
            return `${GET_FILE_BY_ID_API_ROUTE}${reply.imageId}&asPreview=true`;
        }

        if (reply.fileId) {
            return `${GET_FILE_BY_ID_API_ROUTE}${reply.fileId}`;
        }

        return "";
    };

    const getReplyLocationUrl = (reply?: IReplyMessageItem): string => {
        if (!reply?.location) return "";
        return getMapOpenUrl(reply.location);
    };

    const replyPreviewComponent = () => {
        const reply = props.messageItem.replyToMessage;
        if (!reply) return null;

        const replyText = getReplyPreviewText(reply);
        const attachmentUrl = getReplyAttachmentUrl(reply);
        const locationUrl = getReplyLocationUrl(reply);

        return (
            <div className={"message-reply-preview " + mrt}>
                <div className="message-reply-preview__title">
                    {getReplyAuthor(reply)}
                    {reply.isDeleted && props.deletedMessagesDisplay == DeletedMessagesDisplay.Default && (<span className="message-reply-preview__deleted">({t("chat.deleted")})</span>)}
                    {reply.isEdited && !reply.isDeleted && (<span className="message-reply-preview__edited">({t("chat.edited")})</span>)}
                </div>
                <div className={"message-reply-preview__text" + (isReplyExpanded ? " message-reply-preview__text--expanded" : "")}>
                    
                    {replyText ? (
                        replyText
                        ) : attachmentUrl ? (
                            <>
                                {t("common.attachment")}:{" "}
                                <a href={attachmentUrl} className="message-reply-preview__link" target="_blank"> {t("nlp.autocompleteOpenText")}</a>
                            </>
                        ) : locationUrl ? (
                            <>
                                {t("common.location")}:{" "}
                                <a href={locationUrl} className="message-reply-preview__link" target="_blank"> {t("nlp.autocompleteOpenText")}</a>
                            </>
                        ) : ( ""
                    )}
                </div>

                {replyText.length > 120 && (
                    <button type="button" className="message-reply-preview__toggle" onClick={() => setIsReplyExpanded(!isReplyExpanded)}>
                        {isReplyExpanded ? t("chat.hide") : t("chat.show")}
                    </button>
                )}
            </div>
        );
    };


    const [quoteMenu, setQuoteMenu] = useState<PopoverPosition | null>(null);
    const [quote, setQuote] = useState<string | null>(null);

    const _onMessageMouseUp = (e: MouseEvent<HTMLDivElement>) => {
        if (props.messageItem.forOperatorsOnly ||
            (props.messageItem.messageType !== MessageType.clientMessage && props.messageItem.messageType !== MessageType.operatorMessage) ||
            props.selectedChat?.channelType !== ChannelType.Widget) {
            return;
        }

        if (e.button !== 0) {
            return;
        }
        const target = e.currentTarget;
        //Use requestAnimationFrame since selection sometemes rests after click
        requestAnimationFrame(() => {
            const selection = document.getSelection();
            const text = selection?.toString();
            if (selection && selection.rangeCount > 0 && text?.trim().length !== 0) {
                const range = selection.getRangeAt(0);

                if (target.contains(range.startContainer) && target.contains(range.endContainer)) {
                    setQuoteMenu(
                        quoteMenu === null
                            ? {
                                left: e.clientX + 2,
                                top: e.clientY - 6,
                            }
                            :
                            null,
                    );
                    setQuote(text ?? null)
                }
            }
        })
    }

    const _handleQuoteMenuClose = () => {
        setQuote(null);
        setQuoteMenu(null);
    }

    const _handleQuote = () => {
        if (quote !== null && quote.length > 0) {
            props.onQuoteMessage(props.messageItem, formatQuote(quote.trim()));
        }
        setQuote(null);
        setQuoteMenu(null);
        document.getSelection()?.removeAllRanges();
    }

    return (
        <div>
            {props.messageItem.messageType !== MessageType.systemMessage ? 
                <div className={"message-item " + mt + (props.messageItem.isFailed ? " failed-message" : "")}>
                    {editButtonComponent()}
                    {props.authorName !== null && (
                        <AuthorName
                            authorName={props.authorName}
                            messageTypeClass={mt}
                            messageType={props.messageItem.messageType}
                            onRemoveOperator={onRemoveOperatorHandler}
                            operatorId={props.operatorId}
                            messageOperatorId={props.messageItem.operatorId ?? 0}
                            chatOwnerId={props.chatOwnerId}
                            isSupervisor={props.isSupervisor}
                            isChatParticipant={props.isAuthorChatParticipant}
                        />
                    )}
                    {replyPreviewComponent()}
                    {retryButtonComponent()}
                    {props.messageItem.imageId !== null &&
                        <div className="btn-rotate-img">
                        <div className="rotate--image">
                                <a href={GET_FILE_BY_ID_API_ROUTE + props.messageItem.imageId + "&asPreview=true"} target="_blank">
                                    <img className="message-item__image" src={GET_FILE_BY_ID_API_ROUTE + props.messageItem.imageId} />
                            </a>
                        </div>
                        </div>}                    
                    {props.messageItem.location !== null && props.messageItem.location !== undefined &&
                        <iframe className="location_iframe" src={getMapUrl(props.messageItem.location)} loading="lazy"></iframe> ||
                        (props.messageItem.isDeleted && props.deletedMessagesDisplay == DeletedMessagesDisplay.Default ?
                            (
                                <div>
                                    <div className="message-text deleted-text" data-testid="message-text" data-deleted="true">
                                        <span className="content">{props.messageItem.text}</span>
                                        <div className="message-button">{buttonDisplay(props.messageItem.buttons)}</div>
                                        {mapFileToComponent(props.messageItem, props.messageItem.contentType)}
                                        <span>({t('chat.deleted')})</span>
                                    </div>
                                </div>
                            )
                            : (
                            <div>
                                <div className="message-text" data-testid="message-text" data-color-mode="light" style={{ whiteSpace: needPreWrap ? "pre-wrap !important" : "normal" }}
                                    onMouseUp={_onMessageMouseUp}>{textContent}
                                    {
                                        !props.messageItem.forOperatorsOnly &&
                                        (props.messageItem.messageType === MessageType.clientMessage || props.messageItem.messageType === MessageType.operatorMessage) &&
                                        <Menu className="message-quote-menu"
                                            open={quoteMenu !== null}
                                            anchorReference='anchorPosition'
                                            anchorPosition={quoteMenu !== null ? quoteMenu : undefined}
                                            onClose={_handleQuoteMenuClose}
                                            slotProps={{ paper: { className: "message-quote-menu__popup" } }}
                                        >
                                            <MenuItem className="message-quote-menu__quote-btn"
                                                onClick={_handleQuote}>{t("chat.quote")}</MenuItem>
                                        </Menu>

                                    }
                                </div>
                                <div className="message-button">{buttonDisplay(props.messageItem.buttons)}</div>
                                    {mapFileToComponent(props.messageItem, props.messageItem.contentType)}
                                </div>
                            )
                        )
                    }

                    <div className="message-footer">
                        <div className="message-footer__left-side">
                            {props.messageItem.messageType !== MessageType.clientMessage && !props.messageItem.forOperatorsOnly && icon}
                            {props.messageItem.messageType === MessageType.clientMessage && props.nlpTopicName.length > 0 && (
                                <div
                                    title={t('chat.nluSearchTooltip')}
                                    className={"message-item__new-kb" + (props.canSearchNlp ? "" : " disable-search")}
                                    onClick={() => {
                                        if(props.canSearchNlp) {
                                            navigate(`${NLP_SEARCH_TOPIC_PATH}/${props.nlpTopicName}`, {
                                                state: {
                                                    theme: props.messageItem.text,
                                                    time: new Date().getTime()
                                                }
                                            });
                                        }
                                    }}
                                >?</div>
                            )}
                        </div>
                        <div className="message-meta">
                            {props.messageItem.isEdited && <div className="message-edited-text"><span>({t('chat.edited')})</span> </div>}
                            <div className="message-time">{getTime(new Date(props.messageItem.timeSent))}
                                {readIcon}
                            </div>
                        </div>
                    </div>
                </div> :
                <div className="system-message-item" data-testid="system-message">
                    <span>{getDateTime(new Date(props.messageItem.timeSent)) + " "}</span>
                    <span><HyperlinkTextHelper>{props.messageItem.text}</HyperlinkTextHelper></span>
                </div>
            }
        </div>
    );
    
}

export default MessageItem;